// Long-tail landing page /stay/<slug> — topic copy in the visitor's language, matching rooms with direct prices, FAQ, schema.
import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Sparkles } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import Header from '../components/Header';
import Footer from '../components/Footer';

const SITE_URL = (process.env.REACT_APP_SITE_URL || 'https://larosa-negra.com').replace(/\/$/, '');
import { useLanguage } from '../context/LanguageContext';
import { useHotel } from '../context/HotelContext';
import { homePath } from '../siteRoutes';
import { getTopics, getTopic, topicRooms } from '../i18n/topics';
import { API_URL } from '../pages/admin/_shared';
import { HolidayWeeks, HOLIDAY_WEEK_SLUGS } from '../components/HolidayWeeks';
import '../components/UlcinjGuide.css';

const TopicPage = ({ slug }) => {
  const { lang } = useLanguage();
  const { rooms } = useHotel();
  const { pathname } = useLocation();
  const T = getTopics(lang);
  const topic = getTopic(lang, slug);
  const prefix = homePath(pathname) === '/' ? '' : homePath(pathname);
  useEffect(() => { window.scrollTo({ top: 0 }); }, [slug]);
  if (!topic) return null;
  const matching = topicRooms(slug, rooms);
  const path = `/stay/${slug}`;
  const schema = { '@context': 'https://schema.org', '@graph': [
    { '@type': 'WebPage', '@id': `${SITE_URL}${prefix}${path}`, name: topic.title, description: topic.metaDescription, inLanguage: lang, isPartOf: { '@id': `${SITE_URL}/#website` }, about: { '@id': `${SITE_URL}/#hotel` } },
    { '@type': 'FAQPage', mainEntity: topic.faq.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })) },
    { '@type': 'BreadcrumbList', itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Hotel La Rosa Negra', item: `${SITE_URL}${prefix}/` },
      { '@type': 'ListItem', position: 2, name: T.nav, item: `${SITE_URL}${prefix}/stay/${T.topics[0].slug}` },
      { '@type': 'ListItem', position: 3, name: topic.title, item: `${SITE_URL}${prefix}${path}` }] },
  ] };
  const roomHero = matching[0]?.images?.[0];
  const hero = TOPIC_HERO[slug] ? `${process.env.PUBLIC_URL || ''}${TOPIC_HERO[slug]}` : (roomHero ? `${API_URL}${roomHero}` : undefined);
  return (
    <div className="bg-white" data-testid="topic-page">
      <SEOHead title={topic.metaTitle} description={topic.metaDescription} url={path} schema={schema} image={hero} />
      <Header />
      <section className="relative bg-gray-900 text-white">
        {hero && <img src={hero} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" fetchPriority="high" />}
        <div className="relative container mx-auto px-4 pt-32 pb-16 lg:pt-40 lg:pb-24">
          <p className="text-orange-400 text-xs font-semibold uppercase tracking-[0.2em] mb-3">{T.nav}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight max-w-3xl break-words [overflow-wrap:anywhere]" data-testid="topic-title">{topic.title}</h1>
          <p className="mt-5 text-base md:text-lg text-gray-200 max-w-2xl">{topic.intro[0]}</p>
          <Link to={`${prefix}/booking`} className="inline-flex items-center gap-2 mt-8 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-full px-6 py-3 transition-colors" data-testid="topic-book">{T.book} <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>
      <section className="container mx-auto px-4 py-12 lg:py-16 grid lg:grid-cols-5 gap-10 overflow-x-hidden">
        <div className="lg:col-span-3 space-y-6 min-w-0">
          <p className="text-gray-700 text-base md:text-lg leading-relaxed guide-dropcap break-words">{topic.intro[1]}</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {topic.facts.map((f, i) => (
              <div key={i} className="rounded-2xl border bg-orange-50/40 p-4" data-testid={`topic-fact-${i}`}>
                <div className="text-[11px] uppercase tracking-wide text-orange-700 font-semibold">{f.label}</div>
                <div className="text-gray-900 font-bold mt-1">{f.value}</div>
              </div>
            ))}
          </div>
          {HOLIDAY_WEEK_SLUGS.includes(slug) && <HolidayWeeks lang={lang} prefix={prefix} />}
          <h2 className="text-base md:text-lg font-bold text-gray-900 pt-4">{T.faqHeading}</h2>
          <div className="divide-y rounded-2xl border">
            {topic.faq.map((f, i) => (
              <details key={i} className="group p-4" data-testid={`topic-faq-${i}`}>
                <summary className="cursor-pointer font-semibold text-gray-900 flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-orange-500 mt-1 shrink-0" />{f.q}</summary>
                <p className="mt-2 text-sm text-gray-600 pl-6">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
        <aside className="lg:col-span-2 min-w-0">
          <h2 className="text-base md:text-lg font-bold text-gray-900 mb-3 flex items-center gap-2"><Sparkles className="w-4 h-4 text-orange-500" /> {T.roomsHeading}</h2>
          <ul className="rounded-2xl border divide-y bg-white shadow-sm" data-testid="topic-rooms">
            {matching.slice(0, 6).map((r) => (
              <li key={r.id}>
                <Link to={`${prefix}/room/${r.id}`} className="flex items-center gap-3 p-3 hover:bg-orange-50/60 transition-colors" data-testid={`topic-room-${r.id}`}>
                  {r.images?.[0] && <img src={`${API_URL}${r.images[0]}`} alt={r.name} className="w-20 h-14 object-cover rounded-lg" loading="lazy" />}
                  <div className="flex-1 min-w-0"><div className="font-semibold text-gray-900 truncate">{r.name}</div><div className="text-xs text-gray-500">{r.size} m² · {r.guests} 👤</div></div>
                  <div className="text-right"><div className="text-[10px] text-gray-500">{T.from}</div><div className="text-lg font-bold text-orange-600">€{r.price}</div><div className="text-[10px] text-gray-500">{T.perNight}</div></div>
                </Link>
              </li>
            ))}
          </ul>
          <h3 className="text-sm font-bold text-gray-900 mt-8 mb-2">{T.otherHeading}</h3>
          <ul className="flex flex-wrap gap-2" data-testid="topic-others">
            {T.topics.filter((x) => x.slug !== slug).map((x) => (
              <li key={x.slug}><Link to={`${prefix}/stay/${x.slug}`} className="inline-block text-xs border rounded-full px-3 py-1.5 text-gray-700 hover:border-orange-400 hover:text-orange-700 transition-colors">{x.title}</Link></li>
            ))}
          </ul>
        </aside>
      </section>
      <Footer />
    </div>
  );
};

// Head-term pages get a destination illustration instead of the cheapest room's photo.
const TOPIC_HERO = { 'hotels-in-ulcinj': '/guide/old-town.jpg', 'velika-plaza-hotels': '/guide/velika-plaza.jpg', 'sea-view-hotel-ulcinj': '/guide/sunset.jpg', 'plazhi-i-madh-ulqin': '/guide/velika-plaza.jpg', 'hotel-ulqin': '/guide/getting-there.jpg', 'halal-friendly-family-hotel-ulcinj': '/guide/velika-plaza.jpg' };

export default TopicPage;
